import React, { useEffect, useState } from 'react'
import { Button, Dropdown, Input, MenuProps, Select } from 'antd'
import { CloseCircleFilled } from '@ant-design/icons'
import { UseFormReturn } from 'react-hook-form'
import { FormLabel } from '../../components/form/FormLabel'
import { WEBHOOK_PRESETS, WEBHOOK_SOURCES, WebhookField, WebhookSource } from './webhookFields'

type Props = {
  formHook: UseFormReturn
  initialFields?: WebhookField[]
}

export const WebhookFieldsEditor = ({ formHook, initialFields }: Props) => {
  const { register, setValue, unregister } = formHook
  const [fields, setFields] = useState<WebhookField[]>(initialFields ?? [])

  useEffect(() => {
    register('fields', { value: fields })
  }, [register, fields])

  useEffect(() => {
    return () => {
      unregister('fields')
    }
  }, [unregister])

  const sync = (next: WebhookField[]) => {
    setFields(next)
    setValue('fields', next)
  }

  const addRow = () => sync([...fields, { key: '', source: 'constant', value: '' }])
  const removeRow = (i: number) => sync(fields.filter((_, idx) => idx !== i))
  const updateRow = (i: number, patch: Partial<WebhookField>) =>
    sync(
      fields.map((f, idx) => {
        if (idx !== i) return f
        const next = { ...f, ...patch }
        if (patch.source && patch.source !== 'constant') delete next.value
        return next
      })
    )

  const presetMenu: MenuProps = {
    items: Object.entries(WEBHOOK_PRESETS).map(([k, p]) => ({ key: k, label: `Load ${p.label} preset` })),
    onClick: ({ key }) => sync(WEBHOOK_PRESETS[key].fields.map((f) => ({ ...f }))),
  }

  return (
    <div className="col-12 mt-4">
      <div className="d-flex justify-content-between align-items-center">
        <div className="">
          <FormLabel label="Payload fields" />
        </div>
        <div>
          <Dropdown menu={presetMenu}>
            <Button type="dashed" className="me-2">
              Load webhook preset
            </Button>
          </Dropdown>
          <Button type="primary" onClick={addRow}>
            Add new field
          </Button>
        </div>
      </div>

      <div className="mt-2">
        {fields.map((f, i) => (
          <div key={i} className="d-flex align-items-center mb-2">
            <Input
              placeholder="Key (e.g. Telefonnummer)"
              value={f.key}
              onChange={(e) => updateRow(i, { key: e.target.value })}
              style={{ flex: 1 }}
            />
            <Select<WebhookSource>
              value={f.source}
              onChange={(v) => updateRow(i, { source: v })}
              options={WEBHOOK_SOURCES.map((s) => ({ value: s.value, label: s.label }))}
              style={{ width: 180, marginLeft: 8 }}
            />
            {f.source === 'constant' ? (
              <Input
                placeholder="Custom value"
                value={f.value ?? ''}
                onChange={(e) => updateRow(i, { value: e.target.value })}
                style={{ width: 200, marginLeft: 8 }}
              />
            ) : null}
            <div className="ms-3 pointer" onClick={() => removeRow(i)}>
              <CloseCircleFilled />
            </div>
          </div>
        ))}
        {fields.length === 0 ? (
          <p className="opacity-50 mt-2 mb-0">No payload fields configured — load a preset or add fields manually.</p>
        ) : null}
      </div>
    </div>
  )
}
